import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { TableModule } from '@openng/optimus-ui/table';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { FileUploadModule } from '@openng/optimus-ui/fileupload';
import { TagModule } from '@openng/optimus-ui/tag';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ToastModule } from '@openng/optimus-ui/toast';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { AuthService } from '../../core/services/auth.service';

import { Release, CreateReleaseRequest, UpdateReleaseRequest } from '../../shared/models/release.model';
import { ReleaseService } from '../../core/services/release.service';
import { ReleasesDownloadsComponent } from './releases-downloads/releases-downloads.component';
import {
  buildCreateReleaseFormData,
  buildRollbackStream,
  buildVersionFilterOptions,
  canRollbackToRelease,
  describeReleaseError,
  editFormForRelease,
  emptyCreateReleaseForm,
  emptyUpdateReleaseForm,
  extractVersionFromFilename,
  getHighestPublishedRelease,
  getReleaseStatusSeverity,
  getReleaseStatusText,
  isValidInstallerFileType,
  isValidVersionFormat,
  RELEASE_VIEW_OPTIONS,
  ReleaseViewMode,
  triggerReleaseDownload,
  validateCreateReleaseForm
} from './releases.helpers';

@Component({
  selector: 'app-releases',
  imports: [
    CommonModule, FormsModule, TableModule, ButtonModule, DialogModule,
    InputTextModule, TextareaModule, FileUploadModule, TagModule,
    ConfirmDialogModule, ToastModule, ProgressSpinnerModule, TooltipModule,
    ReleasesDownloadsComponent
  ],
  providers: [ConfirmationService, MessageService],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './releases.component.html'
})
export class ReleasesComponent implements OnInit {
  activeView: ReleaseViewMode = 'releases';
  readonly viewOptions = RELEASE_VIEW_OPTIONS;
  private releaseService = inject(ReleaseService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private auth = inject(AuthService);

  releases: Release[] = [];
  loading = false;
  creating = false;
  updating = false;
  canManageReleases = false;

  selectedDownloadVersion: string | null = null;
  versionFilterOptions: { label: string; value: string | null }[] = [{ label: 'All Versions', value: null }];

  showCreateDialog = false;
  showEditDialog = false;
  showNotesDialog = false;

  createForm: CreateReleaseRequest = emptyCreateReleaseForm();
  editForm: UpdateReleaseRequest = emptyUpdateReleaseForm();

  selectedRelease: Release | null = null;
  uploadedFile: File | null = null;
  versionExists = false;

  ngOnInit(): void {
    const role = this.auth.getCurrentUser()?.systemRole ?? 0;
    this.canManageReleases = role === 1 || role === 2;
    this.loadReleases();
  }

  setView(view: ReleaseViewMode): void { this.activeView = view; }

  filterByVersion(version: string): void {
    this.selectedDownloadVersion = version;
    this.activeView = 'downloads';
  }

  loadReleases(): void {
    this.loading = true;
    this.releaseService.getReleases().subscribe({
      next: (releases) => {
        this.releases = releases ?? [];
        this.versionFilterOptions = buildVersionFilterOptions(this.releases);
        this.loading = false;
      },
      error: () => {
        this.toast('error', 'Error', 'Failed to load releases');
        this.loading = false;
      }
    });
  }

  openCreateDialog(): void { this.resetCreateForm(); this.showCreateDialog = true; }
  closeCreateDialog(): void { this.showCreateDialog = false; this.resetCreateForm(); }
  openEditDialog(release: Release): void { this.editForm = editFormForRelease(release); this.showEditDialog = true; }
  openNotesDialog(release: Release): void { this.selectedRelease = release; this.showNotesDialog = true; }

  onFileSelect(event: any): void {
    const file = event.files[0];
    if (!file) return;
    if (!isValidInstallerFileType(file)) {
      this.toast('error', 'Invalid File Type', 'Only MSI and EXE files are allowed.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      this.toast('error', 'File Too Large', 'File size must be less than 100MB.');
      return;
    }

    this.uploadedFile = file;
    const version = extractVersionFromFilename(file.name);
    if (version) {
      this.createForm.version = version;
      this.checkVersionExists(version);
    } else {
      this.toast('warn', 'Version Not Detected', 'Could not detect version from filename. You can enter it manually.');
    }
  }

  checkVersionExists(version: string): void {
    if (!version) { this.versionExists = false; return; }
    const existing = this.releases.find(r => r.version === version.trim());
    this.versionExists = !!existing;
    if (existing) {
      this.toast('warn', 'Version Already Exists', `Release version ${version} already exists. Please use a different version.`);
    }
  }

  createRelease(): void {
    const validationError = validateCreateReleaseForm(this.createForm, this.uploadedFile);
    if (validationError || this.versionExists) {
      this.toast('error', 'Validation Error', validationError || 'This version already exists. Please choose a different version.');
      return;
    }

    this.creating = true;
    this.releaseService.createReleaseWithFile(buildCreateReleaseFormData(this.createForm, this.uploadedFile)).subscribe({
      next: () => {
        this.toast('success', 'Success', 'Release created successfully');
        this.loadReleases();
        this.closeCreateDialog();
        this.creating = false;
      },
      error: (error) => {
        this.toast('error', 'Error', error.error?.message || 'Failed to create release');
        this.creating = false;
      }
    });
  }

  updateRelease(): void {
    const version = this.editForm.version?.trim();
    if (!version || this.updating) return;

    if (!isValidVersionFormat(version)) {
      this.toast('error', 'Validation Error', 'Version must be in format major.minor.patch (e.g., 2.0.0 or 2.0.0.0)');
      return;
    }

    this.updating = true;
    this.releaseService.updateRelease(this.editForm.id, { ...this.editForm, version }).subscribe({
      next: () => {
        this.toast('success', 'Success', 'Release updated successfully');
        this.loadReleases();
        this.showEditDialog = false;
        this.updating = false;
      },
      error: (error) => {
        this.toast('error', 'Error', describeReleaseError(error, 'Failed to update release'));
        this.updating = false;
      }
    });
  }

  publishRelease(release: Release): void {
    this.confirmThen(`Are you sure you want to publish version ${release.version}?`, 'Confirm Publish',
      this.releaseService.publishRelease(release.id), 'Release published successfully', 'Failed to publish release');
  }

  unpublishRelease(release: Release): void {
    this.confirmThen(`Are you sure you want to unpublish version ${release.version}?`, 'Confirm Unpublish',
      this.releaseService.unpublishRelease(release.id), 'Release unpublished successfully', 'Failed to unpublish release');
  }

  deleteRelease(release: Release): void {
    this.confirmThen(`Are you sure you want to delete version ${release.version}? This action cannot be undone.`,
      'Confirm Delete', this.releaseService.deleteRelease(release.id), 'Release deleted successfully', 'Failed to delete release', true);
  }

  rollbackToRelease(release: Release): void {
    this.confirmationService.confirm({
      message: `CRITICAL: Do you want to rollback all active clients to v${release.version}? This will force-downgrade any newer versions.`,
      header: 'Emergency Rollback Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        buildRollbackStream(release, this.releases, this.releaseService).subscribe({
          next: () => {
            this.toast('warn', 'Rollback Active', `All clients will now rollback to v${release.version}`);
            this.loadReleases();
          },
          error: (err) => {
            this.toast('error', 'Rollback Failed', err?.error?.message || `Failed to execute rollback to v${release.version}`);
            this.loadReleases();
          }
        });
      }
    });
  }

  canRollbackTo(release: Release): boolean { return canRollbackToRelease(release, this.releases); }
  getHighestPublishedRelease(): Release | null { return getHighestPublishedRelease(this.releases); }
  getStatusSeverity(isPublished: boolean): 'success' | 'warn' { return getReleaseStatusSeverity(isPublished); }
  getStatusText(isPublished: boolean): string { return getReleaseStatusText(isPublished); }
  formatFileSize(bytes: number): string { return this.releaseService.formatFileSize(bytes); }

  downloadRelease(release: Release): void {
    if (!release.fileUrl) {
      this.toast('warn', 'Download Unavailable', 'File URL not available for this release');
      return;
    }
    this.releaseService.recordDownload({ releaseId: release.id, version: release.version, source: 'CMS' }).subscribe({ error: () => {} });
    triggerReleaseDownload(release.fileUrl, release.fileName, release.version);
    this.toast('info', 'Download Started', `Downloading ${release.fileName}`);
  }

  /** Shared confirm + server action flow for publish/unpublish/delete. */
  private confirmThen(
    message: string, header: string, task$: Observable<unknown>,
    successDetail: string, failureDetail: string, danger = false
  ): void {
    this.confirmationService.confirm({
      message, header, icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: danger ? 'p-button-danger' : undefined,
      accept: () => {
        task$.subscribe({
          next: () => { this.toast('success', 'Success', successDetail); this.loadReleases(); },
          error: (err) => { this.toast('error', 'Error', describeReleaseError(err, failureDetail)); }
        });
      }
    });
  }

  private toast(severity: 'success' | 'warn' | 'error' | 'info', summary: string, detail: string): void {
    this.messageService.add({ severity, summary, detail });
  }

  private resetCreateForm(): void {
    this.createForm = emptyCreateReleaseForm();
    this.uploadedFile = null;
    this.versionExists = false;
    this.creating = false;
  }
}
